import { Box, useRadio, forwardRef } from '@chakra-ui/react-v2';
import type { UseRadioProps } from '@chakra-ui/react-v2';

import { useSegmentControlStyles } from './SegmentControlStylesContext';
import { useSegmentControlContext } from './utils/SegmentControlContext';

export type SegmentProps = UseRadioProps;

/**
 * A single radio button for the multipart SegmentControl component.
 */
export const Segment = forwardRef<SegmentProps, 'div'>(
  ({ value, isDisabled, children, ...props }, ref) => {
    // Get multipart styling
    const {
      segmentContainer: segmentContainerStyles,
      segmentButton: segmentButtonStyles,
    } = useSegmentControlStyles();

    // Functionality from useRadioGroup chakra hook moved internal to the component.
    const { getRadioProps, isDisabled: groupIsDisabled } =
      useSegmentControlContext();
    const radioProps = getRadioProps({ value });

    // Standard inclusions for custom radio buttons per chakra docs [merged with useRadioGroup props]
    const { getInputProps, getCheckboxProps, htmlProps } = useRadio({
      isDisabled: isDisabled || groupIsDisabled,
      ...radioProps,
      ...props,
    });

    const inputProps = getInputProps();
    const checkboxProps = getCheckboxProps();

    return (
      <Box __css={segmentContainerStyles} ref={ref} as="label" {...htmlProps}>
        <input {...inputProps} />
        <Box __css={segmentButtonStyles} {...checkboxProps}>
          {children}
        </Box>
      </Box>
    );
  },
);
Segment.displayName = 'Segment';
